// Создание, правка и забывание записи памяти.
//
// Основной путь создания — агент инструментом `write_memory` после
// подтверждения пользователя; дополнительно пользователь может завести факт
// явным путём «Запомнить» из чата (useCreateMemoryMutation). Здесь — завести
// факт вручную, исправить формулировку и убрать ошибочное: иначе неверный факт
// молча подмешивался бы в каждый разговор по сделке.
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useScopeStore } from '../../../shared/scope/useCurrentScope.js';

import {
  createMemoryRecord,
  forgetMemoryRecord,
  updateMemoryRecord,
  type CreateMemoryInput,
  type MemoryRecordDto,
  type UpdateMemoryInput,
} from '../api/memory.client.js';

/**
 * Инвалидация по корню `memory`: правка одной записи задевает список своего
 * уровня и выдачу поиска, а они лежат под разными ключами.
 */
function useInvalidateMemory(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['memory'] });
}

interface CreateMemoryMutationContext {
  workspaceId: string;
}

export function useCreateMemoryMutation() {
  const invalidate = useInvalidateMemory();

  return useMutation<MemoryRecordDto, Error, CreateMemoryInput, CreateMemoryMutationContext>({
    mutationFn: (input) => createMemoryRecord(input),
    onMutate: () => ({
      workspaceId: useScopeStore.getState().scope?.workspaceId ?? 'anonymous',
    }),
    onSuccess: (_record, _input, context) => {
      const scopeState = useScopeStore.getState();
      if (
        scopeState.isSwitchingWorkspace ||
        scopeState.scope?.workspaceId !== context.workspaceId
      ) {
        return;
      }
      void invalidate();
    },
  });
}

export function useUpdateMemoryMutation() {
  const invalidate = useInvalidateMemory();

  return useMutation<MemoryRecordDto, Error, { id: string; input: UpdateMemoryInput }>({
    mutationFn: ({ id, input }) => updateMemoryRecord(id, input),
    onSuccess: () => invalidate(),
  });
}

export function useForgetMemoryMutation() {
  const invalidate = useInvalidateMemory();

  return useMutation<void, Error, string | { id: string; expectedUpdatedAt: string }>({
    mutationFn: (input) =>
      typeof input === 'string'
        ? forgetMemoryRecord(input)
        : forgetMemoryRecord(input.id, input.expectedUpdatedAt),
    onSuccess: () => invalidate(),
  });
}
